import { useCallback, useEffect, useState } from 'react';
import { Accordion, Col, Form, Row, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudArrowUp, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/base/Button';
import { useDispatch } from 'react-redux';
import { setToast } from 'components/Toasts/data-access/toast-reducer';
import {
  ATAC_DEPLOYMENT_TYPES,
  ATAC_DOCUMENT_TYPES,
  ATACProject,
  ATACDocument,
  ATACProvider,
  ATAC_RISK_CLASSIFICATIONS,
  deleteATACDocument,
  listATACDocuments,
  listATACProviders,
  uploadATACDocument
} from 'data-access/atac.api';

const ACCEPTED_FILE_TYPES = '.pdf,.docx,.txt';

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return Number.isNaN(d.getTime()) ? dateStr : d.toLocaleString();
};

const truncateUrl = (url: string, max = 48) => (url.length > max ? `${url.slice(0, max)}…` : url);

interface DocumentsTabProps {
  projectId: string;
  project: ATACProject | null;
  documents: ATACDocument[];
  onDocumentsChange?: () => void;
}

const DocumentsTab = ({ projectId, project, documents, onDocumentsChange }: DocumentsTabProps) => {
  const dispatch = useDispatch();
  const [providers, setProviders] = useState<ATACProvider[]>([]);
  const [localDocuments, setLocalDocuments] = useState<ATACDocument[]>(documents);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [registrationCollapsed, setRegistrationCollapsed] = useState(false);

  const [provider, setProvider] = useState(project?.provider ?? '');
  const [modelName, setModelName] = useState(project?.modelName ?? '');
  const [modelVersion, setModelVersion] = useState(project?.modelVersion ?? '');
  const [deploymentType, setDeploymentType] = useState(project?.deploymentType ?? '');
  const [intendedUse, setIntendedUse] = useState(project?.intendedUse ?? '');
  const [riskClassification, setRiskClassification] = useState(project?.riskClassification ?? '');
  const [assessmentOwner, setAssessmentOwner] = useState(project?.assessmentOwner ?? '');

  const [documentType, setDocumentType] = useState<string>(ATAC_DOCUMENT_TYPES[0]);
  const [file, setFile] = useState<File | null>(null);
  const [sourceUrl, setSourceUrl] = useState('');

  useEffect(() => {
    setLocalDocuments(documents);
  }, [documents]);

  useEffect(() => {
    if (project) {
      setProvider(project.provider ?? '');
      setModelName(project.modelName ?? '');
      setModelVersion(project.modelVersion ?? '');
      setDeploymentType(project.deploymentType ?? '');
      setIntendedUse(project.intendedUse ?? '');
      setRiskClassification(project.riskClassification ?? '');
      setAssessmentOwner(project.assessmentOwner ?? '');
    }
  }, [project]);

  useEffect(() => {
    if (project?.provider && project?.modelName) {
      setRegistrationCollapsed(true);
    }
  }, [project?.provider, project?.modelName]);

  useEffect(() => {
    listATACProviders()
      .then(res => setProviders(res.data?.providers ?? []))
      .catch(() => setProviders([]));
  }, []);

  const selectedProviderObj = providers.find(p => p.provider === provider);
  const modelOptions = selectedProviderObj?.models ?? [];
  const selectedModelObj = modelOptions.find(m => m.name === modelName);
  const versionOptions = selectedModelObj?.versions ?? [];

  const loadDocuments = useCallback(async () => {
    if (!projectId) return;
    setIsLoading(true);
    try {
      const response = await listATACDocuments(projectId);
      setLocalDocuments(response.data?.documents ?? []);
      onDocumentsChange?.();
    } catch {
      setLocalDocuments([]);
    } finally {
      setIsLoading(false);
    }
  }, [projectId, onDocumentsChange]);

  const handleProviderChange = (value: string) => {
    setProvider(value);
    setModelName('');
    setModelVersion('');
  };

  const handleModelChange = (value: string) => {
    setModelName(value);
    setModelVersion('');
  };

  const handleUpload = async () => {
    if (!file && !sourceUrl.trim()) {
      dispatch(
        setToast({
          show: true,
          title: 'Missing evidence',
          message: 'Provide a file or source URL to upload.'
        })
      );
      return;
    }

    const formData = new FormData();
    if (file) formData.append('file', file);
    if (sourceUrl.trim()) formData.append('sourceUrl', sourceUrl.trim());
    formData.append('documentType', documentType);
    formData.append('providerName', provider);
    formData.append('modelName', modelName);
    formData.append('modelVersion', modelVersion);
    formData.append('deploymentType', deploymentType);
    formData.append('intendedUse', intendedUse);
    formData.append('riskClassification', riskClassification);
    formData.append('assessmentOwner', assessmentOwner);

    setIsUploading(true);
    try {
      await uploadATACDocument(projectId, formData);
      setFile(null);
      setSourceUrl('');
      await loadDocuments();
      if (provider && modelName) setRegistrationCollapsed(true);
      dispatch(
        setToast({
          show: true,
          title: 'Document uploaded',
          message: 'Evidence document has been uploaded successfully.'
        })
      );
    } catch (err: any) {
      dispatch(
        setToast({
          show: true,
          title: 'Upload failed',
          message: err?.response?.data?.message || err?.message || 'Could not upload document.'
        })
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (evidenceId: string) => {
    try {
      await deleteATACDocument(projectId, evidenceId);
      await loadDocuments();
      dispatch(
        setToast({
          show: true,
          title: 'Document deleted',
          message: 'Evidence document has been removed.'
        })
      );
    } catch (err: any) {
      dispatch(
        setToast({
          show: true,
          title: 'Delete failed',
          message: err?.response?.data?.message || err?.message || 'Could not delete document.'
        })
      );
    }
  };

  return (
    <div>
      <h5 className="fw-semibold mb-1">Evidence documents</h5>
      <p className="text-muted small mb-4">
        Register your model and upload supporting evidence for ATAC trust evaluation.
      </p>

      <Accordion activeKey={registrationCollapsed ? undefined : '0'} className="mb-4">
        <Accordion.Item eventKey="0">
          <Accordion.Header onClick={() => setRegistrationCollapsed(prev => !prev)}>
            Model Registration
          </Accordion.Header>
          <Accordion.Body>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Provider</Form.Label>
                  <Form.Select value={provider} onChange={e => handleProviderChange(e.target.value)}>
                    <option value="">Select provider</option>
                    {providers.map(p => (
                      <option key={p.provider} value={p.provider}>
                        {p.displayName || p.provider}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Model Name</Form.Label>
                  <Form.Select value={modelName} onChange={e => handleModelChange(e.target.value)} disabled={!provider}>
                    <option value="">Select model</option>
                    {modelOptions.map(m => (
                      <option key={m.name} value={m.name}>
                        {m.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Model Version</Form.Label>
                  <Form.Select
                    value={modelVersion}
                    onChange={e => setModelVersion(e.target.value)}
                    disabled={!modelName}
                  >
                    <option value="">Select version</option>
                    {versionOptions.map(v => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Deployment Type</Form.Label>
                  <Form.Select value={deploymentType} onChange={e => setDeploymentType(e.target.value)}>
                    <option value="">Select deployment type</option>
                    {ATAC_DEPLOYMENT_TYPES.map(t => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Risk Classification</Form.Label>
                  <Form.Select value={riskClassification} onChange={e => setRiskClassification(e.target.value)}>
                    <option value="">Select risk level</option>
                    {ATAC_RISK_CLASSIFICATIONS.map(r => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Assessment Owner</Form.Label>
                  <Form.Control
                    type="text"
                    value={assessmentOwner}
                    onChange={e => setAssessmentOwner(e.target.value)}
                    placeholder="Enter assessment owner"
                  />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label>Intended Use</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={intendedUse}
                    onChange={e => setIntendedUse(e.target.value)}
                    placeholder="Describe intended use"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      <div className="rounded-3 border p-4 bg-white mb-4">
        <h6 className="fw-semibold mb-3">Upload evidence</h6>
        <Row className="g-3 align-items-end">
          <Col md={4}>
            <Form.Group>
              <Form.Label>Document Type</Form.Label>
              <Form.Select value={documentType} onChange={e => setDocumentType(e.target.value)}>
                {ATAC_DOCUMENT_TYPES.map(t => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>File ({ACCEPTED_FILE_TYPES})</Form.Label>
              <Form.Control
                type="file"
                accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                onChange={e => {
                  const input = e.target as HTMLInputElement;
                  setFile(input.files?.[0] ?? null);
                }}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Source URL (optional)</Form.Label>
              <Form.Control
                type="url"
                value={sourceUrl}
                onChange={e => setSourceUrl(e.target.value)}
                placeholder="https://..."
              />
            </Form.Group>
          </Col>
          <Col xs={12}>
            <Button variant="primary" className="csa-upload-btn" disabled={isUploading} onClick={handleUpload}>
              <FontAwesomeIcon icon={faCloudArrowUp} className="me-2" />
              {isUploading ? 'Uploading…' : 'Upload Document'}
            </Button>
          </Col>
        </Row>
      </div>

      <div className="rounded-3 border bg-white overflow-hidden">
        <Table responsive hover className="mb-0 align-middle">
          <thead className="table-light">
            <tr>
              <th>#</th>
              <th>Document Type</th>
              <th>File / URL</th>
              <th>Uploaded</th>
              <th>Delete</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={5} className="text-center text-muted py-4">
                  Loading documents…
                </td>
              </tr>
            ) : localDocuments.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center text-muted py-4">
                  No documents uploaded yet. Upload a Model Card or other evidence to get started.
                </td>
              </tr>
            ) : (
              localDocuments.map((doc, index) => (
                <tr key={doc.evidenceId}>
                  <td>{index + 1}</td>
                  <td>
                    <span className="badge bg-primary-subtle text-primary border border-primary-subtle">
                      {doc.documentType}
                    </span>
                  </td>
                  <td className="small">{doc.filename || (doc.sourceUrl ? truncateUrl(doc.sourceUrl) : '—')}</td>
                  <td className="text-muted small">{formatDate(doc.uploadedAt)}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-link text-danger p-0"
                      onClick={() => handleDelete(doc.evidenceId)}
                      aria-label="Delete document"
                    >
                      <FontAwesomeIcon icon={faTrashCan} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default DocumentsTab;
